"use client";

import { Suspense, useEffect, useState } from "react";
import { Loader2, Send, CheckCircle } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import { BackLink } from "@/components/back-link";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ActionContext {
  id: string;
  action: string;
  notes: string | null;
  createdAt: string;
  report: {
    id: string;
    targetType: string;
    reportReason: string;
    reportedAt: string;
  } | null;
}

const APPEAL_TYPE_FOR_ACTION: Record<string, string> = {
  WARN: "warning_appeal",
  SUSPEND: "account_suspension",
  DELETE_CONTENT: "content_removal",
};

export default function AppealPage() {
  return (
    <Suspense fallback={<div className="container mx-auto py-12 text-center text-sm text-gray-500">Loading…</div>}>
      <AppealPageInner />
    </Suspense>
  );
}

function AppealPageInner() {
  const { user, isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  const actionIdParam = searchParams.get("action");

  // Determine the appropriate dashboard URL based on user role. STUDENT and
  // TUTOR roles land on `/student` and `/tutor` directly — there is no
  // `/dashboard` subpath for them. The other roles do have nested dashboards.
  const getDashboardUrl = () => {
    if (!user) return "/";
    switch (user.role) {
      case "ADMIN":
        return "/admin/dashboard";
      case "TUTOR":
        return "/tutor";
      case "STUDENT":
        return "/student";
      case "EMPLOYEE":
        return "/employee/dashboard";
      case "ADVERTISER":
        return "/advertiser/dashboard";
      default:
        return "/";
    }
  };
  const [appealType, setAppealType] = useState("");
  const [appealReason, setAppealReason] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);
  const [error, setError] = useState("");
  const [actionContext, setActionContext] = useState<ActionContext | null>(null);
  const [actionContextLoading, setActionContextLoading] = useState(false);

  useEffect(() => {
    if (!actionIdParam || !isAuthenticated) {
      setActionContext(null);
      return;
    }
    let cancelled = false;
    setActionContextLoading(true);
    fetch(`/api/appeals/action-context?actionId=${encodeURIComponent(actionIdParam)}`)
      .then(async (r) => (r.ok ? r.json() : Promise.reject(await r.json().catch(() => ({})))))
      .then((data) => {
        if (cancelled) return;
        const ctx: ActionContext | null = data?.action ?? null;
        setActionContext(ctx);
        if (ctx) {
          const mapped = APPEAL_TYPE_FOR_ACTION[ctx.action];
          if (mapped) setAppealType(mapped);
        }
      })
      .catch(() => {
        if (!cancelled) setActionContext(null);
      })
      .finally(() => {
        if (!cancelled) setActionContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actionIdParam, isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appealType || !appealReason) {
      setError("Please select an appeal type and provide a reason.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/appeals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appealType,
          appealReason,
          additionalInfo,
          email: user?.email,
          username: user?.name,
          moderationActionId: actionContext?.id ?? actionIdParam ?? undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Something went wrong.");
      }

      setSubmitStatus("success");
      setAppealType("");
      setAppealReason("");
      setAdditionalInfo("");
    } catch (err: any) {
      setSubmitStatus("error");
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto py-12 text-center">
        <Alert variant="destructive">
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You must be logged in to submit an appeal. Please{' '}
            <Link href="/login" className="font-bold underline">
              log in
            </Link>
            .
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (submitStatus === "success") {
    return (
      <div className="container mx-auto py-12 max-w-2xl text-center">
        <Card>
          <CardContent className="p-10">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Appeal Submitted</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for submitting your appeal. Our team will review it and get back to you within 1-3 business days.
            </p>
            <BackLink href={getDashboardUrl()}>Back to Dashboard</BackLink>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 max-w-2xl">
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Submit an Appeal</CardTitle>
            <CardDescription>
              If you believe an action taken on your account was in error, please use this form to submit an appeal. Provide as much detail as possible.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {actionContextLoading && (
              <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                Loading the decision you're appealing…
              </div>
            )}
            {actionContext && !actionContextLoading && (
              <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm text-blue-900">
                <div className="text-[11px] uppercase tracking-wide text-blue-700 mb-1">
                  You are appealing
                </div>
                <div className="font-medium">
                  {actionContext.action === "WARN" && "A warning issued by a moderator"}
                  {actionContext.action === "SUSPEND" && "An account suspension"}
                  {actionContext.action === "DELETE_CONTENT" && "Content removal"}
                  {!["WARN", "SUSPEND", "DELETE_CONTENT"].includes(actionContext.action) &&
                    `Moderation action (${actionContext.action})`}
                </div>
                <div className="text-xs text-blue-800 mt-0.5">
                  Issued {new Date(actionContext.createdAt).toLocaleDateString()}
                  {actionContext.notes ? ` · "${actionContext.notes}"` : ""}
                </div>
              </div>
            )}
            <div className="grid w-full gap-1.5">
              <Label htmlFor="appeal-type">Appeal Type</Label>
              <Select value={appealType} onValueChange={setAppealType} disabled={isSubmitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Select the type of appeal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account_suspension">Account Suspension</SelectItem>
                  <SelectItem value="content_removal">Content Removal</SelectItem>
                  <SelectItem value="warning_appeal">Warning Appeal</SelectItem>
                  <SelectItem value="ban_appeal">Ban Appeal</SelectItem>
                  <SelectItem value="moderation_action">Moderation Action</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="appeal-reason">
                Reason for Appeal
              </Label>
              <Textarea
                id="appeal-reason"
                placeholder="Please explain why you are appealing this action..."
                rows={6}
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="additional-info">
                Additional Information (Optional)
              </Label>
              <Textarea
                id="additional-info"
                placeholder="Any additional context, evidence, or information that supports your appeal..."
                rows={4}
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" /> Submit Appeal
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
